import { Box, Button, Flex, Heading, Stack, Table, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import type { Order } from '@planner/shared';
import { useAuth } from '../hooks/useAuth.js';
import { getOrder } from '../lib/api.js';
import { formatCurrency } from '../lib/format.js';

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Text fontSize="xs" color="fg.muted" textTransform="uppercase">
        {label}
      </Text>
      <Text fontWeight="medium">{value}</Text>
    </Box>
  );
}

export function OrderDetail() {
  const { reference = '' } = useParams();
  const { getToken } = useAuth();
  const [order, setOrder] = useState<Order>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    getToken()
      .then((token) => getOrder(token, reference))
      .then((result) => setOrder(result.order))
      .catch((cause: unknown) => setError((cause as Error).message));
  }, [getToken, reference]);

  if (error) {
    return (
      <Stack gap={4}>
        <Text color="red.500" data-name="order-error">
          {error}
        </Text>
        <RouterLink to="/orders">
          <Button variant="outline" data-name="back-to-orders">
            Back to orders
          </Button>
        </RouterLink>
      </Stack>
    );
  }

  if (!order) {
    return <Text color="fg.muted">Loading order {reference}…</Text>;
  }

  return (
    <Stack gap={6} data-name="order-detail">
      <Flex align="center" gap={4}>
        <Heading size="lg">Order {order.referenceCode}</Heading>
        <RouterLink to="/orders">
          <Button size="sm" variant="outline" ml="auto" data-name="back-to-orders">
            Back to orders
          </Button>
        </RouterLink>
      </Flex>

      <Flex gap={10} wrap="wrap" bg="white" p={5} borderRadius="lg" boxShadow="sm">
        <Field label="Status" value={order.status} />
        <Field label="Region" value={order.region.toUpperCase()} />
        <Field label="Brand" value={order.brand} />
        <Field
          label="Channel"
          value={
            order.channel.type === 'bunnings'
              ? `Bunnings — ${order.channel.storeName ?? order.channel.storeId}`
              : 'Direct'
          }
        />
        <Field label="Created" value={new Date(order.createdAt).toLocaleString()} />
        <Field label="Updated" value={new Date(order.updatedAt).toLocaleString()} />
        {order.appVersion && <Field label="App version" value={order.appVersion} />}
      </Flex>

      <Box bg="white" p={5} borderRadius="lg" boxShadow="sm">
        <Heading size="md" mb={3}>
          Customer
        </Heading>
        <Flex gap={10} wrap="wrap">
          <Field label="Name" value={order.customer.name ?? '—'} />
          <Field label="Email" value={order.customer.email ?? '—'} />
          <Field label="Phone" value={order.customer.phone ?? '—'} />
          <Field label="Postcode" value={order.customer.postcode ?? '—'} />
        </Flex>
      </Box>

      <Box bg="white" p={5} borderRadius="lg" boxShadow="sm">
        <Heading size="md" mb={3}>
          Items
        </Heading>
        <Table.Root size="sm">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>SKU</Table.ColumnHeader>
              <Table.ColumnHeader>Description</Table.ColumnHeader>
              <Table.ColumnHeader>Options</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="right">Qty</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="right">Unit</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="right">Line</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {order.items.map((item, index) => (
              <Table.Row key={`${item.sku}-${index}`}>
                <Table.Cell>{item.sku}</Table.Cell>
                <Table.Cell>{item.description}</Table.Cell>
                <Table.Cell>
                  {item.options
                    ? Object.entries(item.options)
                        .map(([key, value]) => `${key}: ${value}`)
                        .join(', ')
                    : '—'}
                </Table.Cell>
                <Table.Cell textAlign="right">{item.quantity}</Table.Cell>
                <Table.Cell textAlign="right">
                  {formatCurrency(item.unitPrice, order.totals.currency)}
                </Table.Cell>
                <Table.Cell textAlign="right">
                  {formatCurrency(item.unitPrice * item.quantity, order.totals.currency)}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
        <Flex justify="flex-end" mt={4}>
          <Stack gap={1} minW="220px" data-name="order-totals">
            <Flex justify="space-between">
              <Text color="fg.muted">Subtotal</Text>
              <Text>{formatCurrency(order.totals.subtotal, order.totals.currency)}</Text>
            </Flex>
            <Flex justify="space-between">
              <Text color="fg.muted">Tax</Text>
              <Text>{formatCurrency(order.totals.tax, order.totals.currency)}</Text>
            </Flex>
            <Flex justify="space-between" fontWeight="bold">
              <Text>Total ({order.totals.currency})</Text>
              <Text>{formatCurrency(order.totals.total, order.totals.currency)}</Text>
            </Flex>
          </Stack>
        </Flex>
      </Box>
    </Stack>
  );
}
